import type { ChladniPreset } from "./uploadedConversationsVisualizerParticles"

export interface PlaceholderBucket {
  count: number
  description: string
  key: string
  label: string
}

export interface PlaceholderLens {
  buckets: PlaceholderBucket[]
  key: "call-topic" | "personas" | "anomalies"
  preset: ChladniPreset
  subtitle: string
  title: string
}

export interface PlaceholderAgent {
  key: "agent-avery" | "agent-blake"
  label: string
  lenses: PlaceholderLens[]
}

export type PlaceholderAgentKey = PlaceholderAgent["key"]

export const PLACEHOLDER_AGENTS: PlaceholderAgent[] = [
  {
    key: "agent-avery",
    label: "Agent Avery",
    lenses: [
      {
        buckets: [
          {
            count: 70,
            description: "General product or workflow intent behind the call.",
            key: "intent",
            label: "Intent",
          },
          {
            count: 55,
            description: "Charges, invoices, plans, and payment questions.",
            key: "billing",
            label: "Billing",
          },
          {
            count: 48,
            description: "Initial configuration and onboarding setup help.",
            key: "product-setup",
            label: "Product Setup",
          },
          {
            count: 42,
            description: "Cancellation requests, retention, and offboarding.",
            key: "cancellation",
            label: "Cancellation",
          },
          {
            count: 35,
            description: "Login, permissions, and account recovery issues.",
            key: "account-access",
            label: "Account Access",
          },
        ],
        key: "call-topic",
        title: "Call Topic",
        subtitle: "what are users calling about?",
        preset: { m: 3, n: 4 },
      },
      {
        buckets: [
          {
            count: 80,
            description: "First-time users learning setup and core workflows.",
            key: "new-customers",
            label: "New Customers",
          },
          {
            count: 72,
            description: "Existing users following up on active accounts.",
            key: "returning-customers",
            label: "Returning Customers",
          },
          {
            count: 58,
            description:
              "Workspace owners handling permissions and governance.",
            key: "administrators",
            label: "Administrators",
          },
          {
            count: 40,
            description:
              "External partner contacts supporting shared customers.",
            key: "partners",
            label: "Partners",
          },
        ],
        key: "personas",
        title: "Personas",
        subtitle: "what kind of users are calling?",
        preset: { m: 5, n: 2 },
      },
      {
        buckets: [
          {
            count: 58,
            description: "Verification failures and blocked sign-in attempts.",
            key: "authentication",
            label: "Authentication",
          },
          {
            count: 47,
            description: "Escalations that stalled before specialist handoff.",
            key: "escalation-drop",
            label: "Escalation Drop",
          },
          {
            count: 41,
            description: "Agent uncertainty due to missing runbook coverage.",
            key: "knowledge-gaps",
            label: "Knowledge Gaps",
          },
          {
            count: 38,
            description: "Calls bouncing between queues without resolution.",
            key: "transfer-loops",
            label: "Transfer Loops",
          },
          {
            count: 34,
            description: "Extended hold periods without caller updates.",
            key: "silent-holds",
            label: "Silent Holds",
          },
          {
            count: 32,
            description: "Promised callbacks missed or not completed.",
            key: "callback-failures",
            label: "Callback Failures",
          },
        ],
        key: "anomalies",
        title: "Anomalies",
        subtitle: "what looks unusual in these calls?",
        preset: { m: 7, n: 4 },
      },
    ],
  },
  {
    key: "agent-blake",
    label: "Agent Blake",
    lenses: [
      {
        buckets: [
          {
            count: 62,
            description:
              "Order updates, shipping ETA checks, and delivery status.",
            key: "fulfillment",
            label: "Fulfillment",
          },
          {
            count: 56,
            description: "Contract terms, renewals, and plan amendments.",
            key: "renewals",
            label: "Renewals",
          },
          {
            count: 50,
            description: "Questions about syncing data with external systems.",
            key: "integrations",
            label: "Integrations",
          },
          {
            count: 45,
            description: "Audit requests and evidence collection support.",
            key: "compliance",
            label: "Compliance",
          },
          {
            count: 37,
            description: "Enablement calls and team workflow coaching.",
            key: "training",
            label: "Training",
          },
        ],
        key: "call-topic",
        title: "Call Topic",
        subtitle: "what are users calling about?",
        preset: { m: 6, n: 3 },
      },
      {
        buckets: [
          {
            count: 74,
            description:
              "Operations leads coordinating queue and staffing changes.",
            key: "operations-leads",
            label: "Operations Leads",
          },
          {
            count: 68,
            description:
              "Support managers reviewing escalations and playbooks.",
            key: "support-managers",
            label: "Support Managers",
          },
          {
            count: 62,
            description:
              "IT administrators handling security and provisioning.",
            key: "it-administrators",
            label: "IT Administrators",
          },
          {
            count: 46,
            description: "Finance reviewers validating billing and approvals.",
            key: "finance-reviewers",
            label: "Finance Reviewers",
          },
        ],
        key: "personas",
        title: "Personas",
        subtitle: "what kind of users are calling?",
        preset: { m: 4, n: 7 },
      },
      {
        buckets: [
          {
            count: 54,
            description:
              "Policy exceptions applied without matching approvals.",
            key: "policy-overrides",
            label: "Policy Overrides",
          },
          {
            count: 46,
            description: "Multi-team handoffs with unclear queue ownership.",
            key: "long-transfers",
            label: "Long Transfers",
          },
          {
            count: 44,
            description: "Resolution blocked by missing role entitlements.",
            key: "missing-permissions",
            label: "Missing Permissions",
          },
          {
            count: 40,
            description: "Extended hold periods without proactive updates.",
            key: "hold-silence",
            label: "Hold Silence",
          },
          {
            count: 35,
            description: "Agent uncertainty due to sparse runbook guidance.",
            key: "knowledge-gaps",
            label: "Knowledge Gaps",
          },
          {
            count: 31,
            description:
              "Committed follow-ups that were not completed on time.",
            key: "missed-follow-ups",
            label: "Missed Follow-ups",
          },
        ],
        key: "anomalies",
        title: "Anomalies",
        subtitle: "what looks unusual in these calls?",
        preset: { m: 8, n: 5 },
      },
    ],
  },
]

const FALLBACK_PLACEHOLDER_AGENT = PLACEHOLDER_AGENTS[0]

const PLACEHOLDER_LENSES: PlaceholderLens[] = FALLBACK_PLACEHOLDER_AGENT.lenses

export function resolvePlaceholderAgent(agentKey: PlaceholderAgentKey) {
  return (
    PLACEHOLDER_AGENTS.find((agent) => agent.key === agentKey) ??
    FALLBACK_PLACEHOLDER_AGENT
  )
}

export function resolvePlaceholderLens(
  agentKey: PlaceholderAgentKey,
  lensKey: PlaceholderLens["key"],
) {
  const agent = resolvePlaceholderAgent(agentKey)
  return (
    agent.lenses.find((lens) => lens.key === lensKey) ??
    agent.lenses[0] ??
    PLACEHOLDER_LENSES[0]
  )
}
